import { z } from 'zod';
import { dayModeSchema, isoDateSchema } from './day';
import { layoutConfigSchema } from './layout';
import { holdConfigSchema, MINUTES_PER_DAY } from './pricing';
import { reservationSourceSchema, reservationStatusSchema } from './reservation';
import { floorStatusSchema, spotStatusSchema } from './spot-status';

// What a tile and the panel need of a booking; the engine itself is Phase 4.
export const mapReservationSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  guestName: z.string(),
  guestPhone: z.string().nullable(),
  startsOn: isoDateSchema,
  endsOn: isoDateSchema,
  holdUntil: z.string(),
  holdMinutes: z.number().int().nonnegative(),
  holdFeeCents: z.number().int().nonnegative(),
  depositCents: z.number().int().nonnegative(),
  arrivalTime: z.string().nullable(),
  notes: z.string().nullable(),
  source: reservationSourceSchema,
  status: reservationStatusSchema,
});

export type MapReservation = z.infer<typeof mapReservationSchema>;

// status is null on a past day: live statuses exist only for today. See §7.
export const mapSpotSchema = z.object({
  id: z.uuid(),
  number: z.number().int().positive(),
  gridRow: z.number().int().min(1),
  gridCol: z.number().int().min(1),
  zoneKey: z.string(),
  staffOnly: z.boolean(),
  // A checkout is being paid for here: held, but nobody's booking yet. §13
  pendingCheckout: z.boolean().default(false),
  status: spotStatusSchema.nullable(),
  occupations: z.number().int().nonnegative(),
  reservations: z.array(mapReservationSchema),
});

export type MapSpot = z.infer<typeof mapSpotSchema>;

// name is the venue's own wording; names carries it in each language it offers.
export const mapZoneSchema = z.object({
  key: z.string(),
  name: z.string(),
  names: z.record(z.string(), z.string()),
  dayPriceCents: z.number().int().nonnegative(),
  holdPerHourCents: z.number().int().nonnegative(),
});

export type MapZone = z.infer<typeof mapZoneSchema>;

export function zoneName(zone: MapZone | undefined, language: string, fallback: string): string {
  if (zone === undefined) return fallback;
  return zone.names[language] ?? zone.name;
}

export const mapVenueSchema = z.object({
  venueId: z.uuid(),
  name: z.string(),
  spotKind: z.string(),
  currency: z.string(),
  timezone: z.string(),
  hold: holdConfigSchema,
  layout: layoutConfigSchema,
  zones: z.array(mapZoneSchema),
});

export type MapVenue = z.infer<typeof mapVenueSchema>;

// nowMinutes is the venue's own clock: what it may still hold today. §9
export const mapResponseSchema = z.object({
  venue: mapVenueSchema,
  date: isoDateSchema,
  today: isoDateSchema,
  nowMinutes: z.number().int().min(0).max(MINUTES_PER_DAY),
  mode: dayModeSchema,
  spots: z.array(mapSpotSchema),
});

export type MapResponse = z.infer<typeof mapResponseSchema>;

export const mapQuerySchema = z.object({ date: isoDateSchema.optional() });

export const statusChangeSchema = z.object({ status: floorStatusSchema });

export type StatusChange = z.infer<typeof statusChangeSchema>;

// One bulk call per Select-mode action; the cap is the pilot's whole beach. §10
export const BULK_STATUS_LIMIT = 400;

export const bulkStatusChangeSchema = z.object({
  spotIds: z.array(z.uuid()).min(1).max(BULK_STATUS_LIMIT),
  status: floorStatusSchema,
});

export type BulkStatusChange = z.infer<typeof bulkStatusChangeSchema>;

export const spotFlagsSchema = z.object({ staffOnly: z.boolean() });

export type SpotFlags = z.infer<typeof spotFlagsSchema>;

// A whole selection kept for regulars, or handed back to the public. §10, §11
export const bulkSpotFlagsSchema = z.object({
  spotIds: z.array(z.uuid()).min(1).max(BULK_STATUS_LIMIT),
  staffOnly: z.boolean(),
});

export type BulkSpotFlags = z.infer<typeof bulkSpotFlagsSchema>;

export const spotsResponseSchema = z.object({ spots: z.array(mapSpotSchema) });

export type SpotsResponse = z.infer<typeof spotsResponseSchema>;

// What moved: staff hands, a booking, the deadline, or the venue's own config.
export const SPOT_CHANGE_REASONS = ['status', 'reservation', 'auto-release', 'settings'] as const;

export const spotChangeReasonSchema = z.enum(SPOT_CHANGE_REASONS);

export type SpotChangeReason = z.infer<typeof spotChangeReasonSchema>;

// Broadcast to the venue's room on every change; the actor is named, never bare.
export const spotChangeSchema = z.object({
  venueId: z.uuid(),
  at: z.string(),
  actorName: z.string().nullable(),
  reason: spotChangeReasonSchema,
  spots: z.array(mapSpotSchema),
});

export type SpotChange = z.infer<typeof spotChangeSchema>;

export const SPOT_CHANGE_EVENT = 'spot.change';

export const JOIN_VENUE_EVENT = 'venue.join';

export function venueRoom(venueId: string): string {
  return `venue:${venueId}`;
}

// Tiles carry the first name only; the panel is where the full booking lives.
export function firstNameOf(guestName: string): string {
  return guestName.trim().split(/\s+/, 1).join('');
}

export function reservationOn(spot: MapSpot): MapReservation | null {
  return spot.reservations[0] ?? null;
}
